import { useMemo } from 'react';
import type { DatePickerProps } from 'react-datepicker';
import BasicDatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import type {
  FieldPath,
  FieldValues,
  UseControllerReturn,
} from 'react-hook-form';

import { endOfDay, startOfDay, subMonths } from 'date-fns';

import './date-picker.css';
import { weekdayNameMap } from './lib/weekday-name-map';
import { CustomHeader } from './ui/custom-header/custom-header';
import { CustomInput } from './ui/custom-input/custom-input';

import { Clickable } from '../clickable';

export type DateRange = {
  startDate: string;
  endDate: string;
};

export type DateRangePreset = { title: React.ReactNode; value: DateRange };

interface Props<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>
  extends
    Omit<
      DatePickerProps,
      | 'name'
      | 'onChange'
      | 'onBlur'
      | 'value'
      | 'selectsRange'
      | 'selectsMultiple'
      | 'formatMultipleDates'
    >,
    UseControllerReturn<TFieldValues, TName> {
  presets?: DateRangePreset[];
}

export function DatePicker<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(props: Props<TFieldValues, TName>) {
  const { field, formState: _formState, presets, ...inputProps } = props;
  const { value } = field;
  const range = useMemo(
    () => ({
      startDate: value?.startDate && new Date(value.startDate),
      endDate: value?.endDate && new Date(value.endDate),
    }),
    [value]
  );

  return (
    <div className="relative">
      <BasicDatePicker
        {...field}
        {...inputProps}
        selectsRange
        allowSameDay
        selected={range.startDate}
        startDate={range.startDate}
        endDate={range.endDate}
        openToDate={subMonths(new Date(), 1)}
        monthsShown={2}
        shouldCloseOnSelect={false}
        wrapperClassName="w-full"
        popperClassName="custom-datepicker"
        dateFormat="MMMM d"
        customInput={<CustomInput />}
        renderCustomHeader={(headerProps) => <CustomHeader {...headerProps} />}
        formatWeekDay={(day) => weekdayNameMap[day]}
        onChange={([startDate, endDate]: [Date | null, Date | null]) => {
          const periodValue = {
            startDate: startDate && startOfDay(startDate).toISOString(),
            endDate: endDate && endOfDay(endDate).toISOString(),
          };

          field.onChange(periodValue);
        }}
      >
        {presets?.length && (
          <div>
            {presets.map((preset) => (
              <div
                className="inline-block p-4"
                key={`${preset.value.startDate}_${preset.value.endDate}`}
              >
                <Clickable onClick={() => field.onChange(preset.value)}>
                  <span className="text-m font-medium text-skyro-blue">
                    {preset.title}
                  </span>
                </Clickable>
              </div>
            ))}
          </div>
        )}
      </BasicDatePicker>
    </div>
  );
}
